import { useCallback, useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { formatFee } from "@/lib/candidate-utils";
import { useAuth } from "@/contexts/AuthContext";
import { ChartLineUp, Users, CurrencyDollar, Trophy, Calendar, Clock, Target, X, ArrowRight } from "@phosphor-icons/react";

const PERIODS = [
  { v: "today", label: "Today" },
  { v: "week", label: "Week" },
  { v: "month", label: "Month" },
  { v: "quarter", label: "Quarter" },
  { v: "ytd", label: "YTD" },
];

export default function Insights() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openScorecardUid, setOpenScorecardUid] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get(`/dashboard/manager?period=${period}`);
      setData(data);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  if (user?.role !== "admin") {
    return <div className="text-sm text-zinc-500">Admin only.</div>;
  }
  if (error) {
    return <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] p-4 rounded-sm">{error}</div>;
  }
  if (!data) {
    return <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading</div>;
  }

  const o = data.overview;

  return (
    <div className="space-y-8" data-testid="insights-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Manager overview</div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl leading-none flex items-center gap-3">
            <ChartLineUp weight="bold" size={40} />
            Insights.
          </h1>
          <div className="text-sm text-zinc-500 mt-3">Company-wide performance for <b className="text-zinc-950">{o.period_label}</b>.</div>
        </div>
        <div className="flex items-center gap-1 flex-wrap" data-testid="period-picker">
          {PERIODS.map((p) => (
            <button
              key={p.v}
              onClick={() => setPeriod(p.v)}
              data-testid={`period-${p.v}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] border rounded-sm ${
                period === p.v ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Company KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="company-kpis">
        <Kpi label="Interviews booked" value={o.interviews_booked} icon={Calendar} />
        <Kpi label="Candidates registered" value={o.candidates_registered} icon={Users} />
        <Kpi label="Placements" value={o.placements} icon={Trophy} accent="emerald" />
        <Kpi label="Written fees" value={formatFee(o.written_fees)} icon={CurrencyDollar} accent="emerald" />
        <Kpi label="Live pipeline" value={formatFee(o.live_pipeline_fees)} icon={CurrencyDollar} sub="Point-in-time" />
        <Kpi label="Booked out" value={o.booked_out ?? 0} icon={Clock} sub="3 confirmed · awaiting outcome" />
        <Kpi label="Conversion rate" value={`${o.conversion_rate}%`} icon={Trophy} sub={`${o.placements} placed / ${o.placements + o.lost_in_period} closed`} />
        <Kpi label="Avg days to placement" value={o.avg_days_to_placement || "—"} icon={Clock} />
        <Kpi label="Avg interviews to place" value={o.avg_interviews_to_placement || "—"} icon={Target} />
      </div>

      {/* R/Y/G snapshot */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="ryg-snapshot">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Active pipeline</div>
            <div className="font-heading font-bold text-lg">{o.active_candidates} active candidates</div>
          </div>
          <div className="text-xs text-zinc-500">Point-in-time · not filtered by period</div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <BucketCard label="Green" value={o.green} className="border-emerald-500 bg-emerald-50 text-emerald-900" />
          <BucketCard label="Yellow" value={o.yellow} className="border-amber-500 bg-amber-50 text-amber-900" />
          <BucketCard label="Red" value={o.red} className="border-[#FF2A2A] bg-red-50 text-[#B91C1C]" />
        </div>
      </div>

      {/* Per-recruiter table */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="per-recruiter-table">
        <div className="p-5 border-b border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Team</div>
          <div className="font-heading font-bold text-xl">Performance by recruiter</div>
          <div className="text-xs text-zinc-500 mt-1">Click a row for a detailed scorecard.</div>
        </div>
        <div className="grid grid-cols-12 px-5 py-2 border-b border-zinc-100 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <div className="col-span-3">Name</div>
          <div className="col-span-2 text-right">Interviews</div>
          <div className="col-span-2 text-right">Placements</div>
          <div className="col-span-3 text-right">Written fees</div>
          <div className="col-span-2 text-right">Conv %</div>
        </div>
        {data.per_recruiter.map((r) => (
          <button
            key={r.user_id}
            onClick={() => setOpenScorecardUid(r.user_id)}
            data-testid={`recruiter-row-${r.user_id}`}
            className="w-full grid grid-cols-12 px-5 py-3 border-b border-zinc-100 items-center hover:bg-zinc-50 text-left last:border-0"
          >
            <div className="col-span-3">
              <div className="font-medium text-sm text-zinc-950">{r.name || "—"}</div>
              <div className="text-[10px] text-zinc-500 truncate">{r.email}</div>
            </div>
            <div className="col-span-2 text-right text-sm">{r.interviews_booked}</div>
            <div className="col-span-2 text-right text-sm">{r.placements}</div>
            <div className="col-span-3 text-right text-sm font-medium">{formatFee(r.written_fees)}</div>
            <div className="col-span-2 text-right text-sm text-zinc-600">{r.conversion_rate}%</div>
          </button>
        ))}
      </div>

      {openScorecardUid && (
        <ScorecardDrawer
          uid={openScorecardUid}
          period={period}
          onClose={() => setOpenScorecardUid("")}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <Icon size={14} weight="bold" className={accent === "emerald" ? "text-emerald-600" : "text-zinc-400"} />
      </div>
      <div className={`font-heading font-black text-3xl tracking-tight ${accent === "emerald" ? "text-emerald-700" : "text-zinc-950"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function BucketCard({ label, value, className = "" }) {
  return (
    <div className={`border rounded-sm p-4 ${className}`} data-testid={`bucket-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className="font-heading font-black text-3xl tracking-tight">{value}</div>
    </div>
  );
}

export function ScorecardDrawer({ uid, period = "month", onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    setData(null);
    setError("");
    api.get(`/team/${uid}/scorecard?period=${period}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((e) => { if (!cancel) setError(getErrorMessage(e)); });
    return () => { cancel = true; };
  }, [uid, period]);

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" onClick={onClose} data-testid="scorecard-drawer">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Scorecard · {period}</div>
            <div className="font-heading font-bold text-xl">
              {data?.user?.name || "Loading..."}
            </div>
            <div className="text-[10px] text-zinc-500">{data?.user?.email}</div>
          </div>
          <button onClick={onClose} data-testid="close-scorecard" className="text-zinc-500 hover:text-zinc-950"><X size={20} weight="bold" /></button>
        </div>
        {error && <div className="m-5 border border-[#FF2A2A] bg-red-50 text-[#B91C1C] p-3 rounded-sm text-sm">{error}</div>}
        {data && (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Kpi label="Interviews booked" value={data.metrics.interviews_booked} icon={Calendar} />
              <Kpi label="Placements" value={data.metrics.placements} icon={Trophy} accent="emerald" />
              <Kpi label="Written fees" value={formatFee(data.metrics.written_fees)} icon={CurrencyDollar} accent="emerald" />
              <Kpi label="Live pipeline" value={formatFee(data.metrics.live_pipeline_fees)} icon={CurrencyDollar} />
              <Kpi label="Active candidates" value={data.metrics.active_candidates} icon={Users} />
              <Kpi label="Below target" value={data.metrics.below_target_count} icon={Target} sub="Short of interview target" />
              <Kpi label="Overdue chases" value={data.metrics.overdue_followups} icon={Clock} sub="Interviews > 24h w/ no outcome" />
              <Kpi label="Conversion rate" value={`${data.metrics.conversion_rate}%`} icon={Trophy} />
              <Kpi label="Avg days to place" value={data.metrics.avg_days_to_placement || "—"} icon={Clock} />
              <Kpi label="Avg interviews to place" value={data.metrics.avg_interviews_to_placement || "—"} icon={Target} />
              <Kpi label="Avg candidate age" value={data.metrics.avg_active_days || "—"} icon={Clock} sub="Days on books" />
              <Kpi label="Target progress" value={`${data.metrics.target_progress_pct}%`} icon={Target} sub={`of ${formatFee(data.user.monthly_target)}`} />
            </div>
            <a
              href={`/team`}
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950"
            >
              Manage this user in Team <ArrowRight size={11} weight="bold" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
